import { Component, h, State, Prop, Element, Watch, Listen } from '@stencil/core';
import WaveSurfer from 'wavesurfer.js';
import { convertSecondsToMinutes } from '../../lib/helpers';
import '../../stories/Components/MessageBubble/AudioMessageBubble/AudioMesaageBubble.js';

@Component({
  tag: 'audio-bubble',
  styleUrl: '',
  shadow: true,
})
export class AudioBubble {
  @Prop() audioUrl: string;
  @Prop() audioWaveColor: string = '#C5C5C5';
  @Prop() audioProgressColor: string = '#4A76FD';
  @Prop() textColor: string = '#4A76FD';
  @Prop() isSelf: boolean = false;
  @Prop() borderRadius: string = '';
  @Prop() bgColor: string = '';
  @Prop() time: string = '';
  @Prop() isRead: Boolean = false;
  @Prop() isDelivered: Boolean = false;
  @Prop() showFooter: Boolean = false;
  @Prop() messageId: String = '';
  @Prop() timeColor: String = '';
  @Prop() reactions: any = {};
  @Prop() messageType: number;

  @State() isPlaying: boolean = false;
  @State() audioDuration: string = '0:00';
  @State() currentAudioId: string = null;
  @State() waveformContainerReady: boolean = false;

  @Element() el: HTMLElement;

  private wavesurfer: any = null;
  private waveformContainer: HTMLElement = null;
  private loadAbortController: AbortController = null;
  private currentLoadingUrl: string = null;

  @State() initializationAttempts: number = 0;
  private initializationInterval: any = null;

  componentWillLoad() {
    if (typeof WaveSurfer !== 'function') {
      console.error('WaveSurfer is not available as a function. Import may be failing.');
    }
  }

  componentDidLoad() {
    if (this.initializationInterval) {
      clearInterval(this.initializationInterval);
    }

    // Start trying to initialize in a loop until successful
    this.initializationInterval = setInterval(() => {
      this.initializationAttempts++;

      // Check if we have the necessary pieces
      if (this.waveformContainer && this.audioUrl) {
        this.initWaveSurfer();

        // If wavesurfer was created successfully, stop the interval
        if (this.wavesurfer) {
          clearInterval(this.initializationInterval);
        }
      } else {
        console.log('Still waiting for:', !this.waveformContainer ? 'waveform container' : '', !this.audioUrl ? 'audio URL' : '');
      }

      // Give up after 10 attempts
      if (this.initializationAttempts >= 10) {
        console.error('Failed to initialize WaveSurfer after 10 attempts');
        clearInterval(this.initializationInterval);
      }
    }, 500); // Try every 500ms
  }

  @Listen('waveformReady')
  handleWaveformReady(event: CustomEvent) {
    this.waveformContainer = event.detail;

    if (this.waveformContainer && this.audioUrl) {
      setTimeout(() => this.initWaveSurfer(), 100);
    }
  }

  initWaveSurfer() {
    if (!this.waveformContainer) {
      console.error('Waveform container reference is missing');
      return false;
    }

    if (!this.waveformContainer.isConnected) {
      console.error('Waveform container is not connected to DOM');
      return false;
    }

    if (!this.audioUrl) {
      console.error('Audio URL is missing');
      return false;
    }

    try {
      // Abort any ongoing loads before destroying
      this.abortCurrentLoad();

      // Destroy existing instance if any
      if (this.wavesurfer) {
        this.wavesurfer.destroy();
        this.wavesurfer = null;
      }

      // Create a new abort controller for this load operation
      this.loadAbortController = new AbortController();
      this.currentLoadingUrl = this.audioUrl;

      // Create a new instance with more basic settings first
      this.wavesurfer = WaveSurfer.create({
        container: this.waveformContainer,
        waveColor: this.isSelf ? '#000000' : '#ffffff',
        progressColor: this.audioProgressColor,
        height: 40,
        barHeight: 1,
        barWidth: 4,
        barRadius: 3,
        barGap: 4,
        normalize: true,
        sampleRate: 60000,
        fetchParams: {
          signal: this.loadAbortController.signal
        }
      });

      if (!this.wavesurfer) {
        console.error('Failed to create WaveSurfer instance');
        return false;
      }

      this.wavesurfer.load(this.audioUrl);

      this.wavesurfer.on('ready', () => {
        // Only update if this is still the current URL
        if (this.currentLoadingUrl === this.audioUrl) {
          const totalDuration = this.wavesurfer.getDuration();
          const getSortedTime = convertSecondsToMinutes(Math.round(totalDuration));
          this.audioDuration = getSortedTime;
        }
      });
      
      this.wavesurfer.on('audioprocess', () => {
        const totalDuration = this.wavesurfer.getCurrentTime();
        const getSortedTime = convertSecondsToMinutes(Math.round(totalDuration));
        this.audioDuration = getSortedTime;
      });
      
      this.wavesurfer.on('finish', () => {
        this.isPlaying = false;
        this.wavesurfer.seekTo(0);
        
        // Add a small delay to ensure seekTo completes before setting audioDuration
        setTimeout(() => {
          const totalDuration = this.wavesurfer.getDuration();
          const getSortedTime = convertSecondsToMinutes(Math.round(totalDuration));
          this.audioDuration = getSortedTime;
        }, 50);
      });
      
      this.wavesurfer.on('error', err => {
        // Only log if it's not an abort error (which we expect when changing URLs)
        if (err.name !== 'AbortError') {
          console.error('WaveSurfer error:', err);
        }
      });

      return true;
    } catch (error) {
      console.error('Error initializing WaveSurfer:', error);
      return false;
    }
  }

  abortCurrentLoad() {
    // Abort any ongoing fetch operations
    if (this.loadAbortController) {
      this.loadAbortController.abort();
      this.loadAbortController = null;
    }
    this.currentLoadingUrl = null;
  }

  disconnectedCallback() {
    this.abortCurrentLoad();
    
    if (this.wavesurfer) {
      this.wavesurfer.destroy();
      this.wavesurfer = null;
    }
    
    this.isPlaying = false;
    
    if (this.initializationInterval) {
      clearInterval(this.initializationInterval);
    }
  }

  pauseAudioHandler = () => {
    console.log('Pause requested, wavesurfer exists:', !!this.wavesurfer);
    if (!this.wavesurfer) return;

    this.isPlaying = false;
    this.wavesurfer.pause();
  };

  playAudioHandler = () => {
    if (!this.wavesurfer) {
      const success = this.initWaveSurfer();

      if (success) {
        // Wait for the 'ready' event before playing
        this.wavesurfer.once('ready', () => {
          this.isPlaying = true;
          this.wavesurfer.play();
        });
      }
      return;
    }

    this.isPlaying = true;
    this.wavesurfer.play();
  };

  @Watch('audioUrl')
  chatChanged(newAudioUrl: string, oldAudioUrl: string) {
    // Only reinitialize if the URL actually changed
    if (newAudioUrl !== oldAudioUrl) {
      // Stop playback before changing URL
      if (this.wavesurfer && this.isPlaying) {
        this.wavesurfer.pause();
        this.isPlaying = false;
      }
      
      // Reset to default duration display
      this.audioDuration = '0:00';
      
      // Short delay to ensure clean state before reinitializing
      setTimeout(() => {
        if (this.waveformContainer && newAudioUrl) {
          this.initWaveSurfer();
        }
      }, 100);
    }
  }

  render() {
    return (
      <audio-bubble-ui
        isPlaying={this.isPlaying}
        audioDuration={this.audioDuration}
        playAudioHandler={this.playAudioHandler}
        pauseAudioHandler={this.pauseAudioHandler}
        isDelivered={this.isDelivered}
        isRead={this.isRead}
        time={this.time}
        borderRadius={this.borderRadius}
        bgColor={this.bgColor}
        textColor={this.textColor}
        isSelf={this.isSelf}
        showFooter={this.showFooter}
        messageId={this.messageId}
        reactions={this.reactions}
        messageType={this.messageType}
        timeColor={this.timeColor}
      ></audio-bubble-ui>
    );
  }
}